import * as z from 'zod/mini';
import {
  PORT_CONNECTORS,
  TEMPLATE_COUNT_DIRECTIONS,
  TEMPLATE_GROUP_MAX_COLS,
  TEMPLATE_GROUP_MAX_ROWS,
  TEMPLATE_GROUP_MIN,
  TEMPLATE_MAX_GROUPS,
  TEMPLATE_MAX_ICONS,
  TEMPLATE_MAX_TEXTS,
  TEMPLATE_MAX_U,
  TEMPLATE_MIN_U,
  TEMPLATE_PORT_NUMBER_MAX,
  TEMPLATE_PREFIX_MAX_LENGTH,
  TEMPLATE_TEXT_MAX_LENGTH,
} from '../constants.ts';

// A device template's face layout (ROADMAP B1): the front and back faces of
// a device model, each a set of port groups, text fields, and icons, at a
// height of 1–8U. A port group is a grid of rows × columns with a connector,
// a name prefix, a counting direction, and a start number, from which the
// port numbers are computed (shared/template.ts). This is what the rack
// renderer (ROADMAP A1, a later wave) draws; here it is the schema, carried
// on a preset as its optional `layout`.

/** left-to-right (row by row) or top-to-bottom (column by column). */
export const countDirectionSchema = z.enum(TEMPLATE_COUNT_DIRECTIONS);
export type CountDirection = (typeof TEMPLATE_COUNT_DIRECTIONS)[number];

const gridSide = (max: number) => z.int().check(z.gte(TEMPLATE_GROUP_MIN), z.lte(max));

export const portGroupSchema = z.object({
  /** The connector every port in the group presents; blank when mixed or unrecorded. */
  connector: z.optional(z.enum(PORT_CONNECTORS)),
  /** What each port's name begins with ("SW", "PP"); the number follows it. */
  prefix: z.string().check(z.maxLength(TEMPLATE_PREFIX_MAX_LENGTH)),
  direction: countDirectionSchema,
  rows: gridSide(TEMPLATE_GROUP_MAX_ROWS),
  cols: gridSide(TEMPLATE_GROUP_MAX_COLS),
  /** The number the first port carries; the rest count up from it. */
  startNumber: z.int().check(z.gte(1), z.lte(TEMPLATE_PORT_NUMBER_MAX)),
});
export type PortGroup = z.infer<typeof portGroupSchema>;

const labelText = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(TEMPLATE_TEXT_MAX_LENGTH));

export const templateTextSchema = z.object({ text: labelText });

export const templateIconSchema = z.object({ icon: labelText });

export const templateFaceSchema = z.object({
  groups: z.array(portGroupSchema).check(z.maxLength(TEMPLATE_MAX_GROUPS)),
  texts: z.array(templateTextSchema).check(z.maxLength(TEMPLATE_MAX_TEXTS)),
  icons: z.array(templateIconSchema).check(z.maxLength(TEMPLATE_MAX_ICONS)),
});
export type TemplateFace = z.infer<typeof templateFaceSchema>;

export const templateLayoutSchema = z.object({
  /** Height in rack units, 1–8U. */
  heightU: z.int().check(z.gte(TEMPLATE_MIN_U), z.lte(TEMPLATE_MAX_U)),
  /** Opt into the port-name rules (ROADMAP C9): uppercase, unique, short display. */
  enforceNames: z.boolean(),
  front: templateFaceSchema,
  back: templateFaceSchema,
});
export type TemplateLayout = z.infer<typeof templateLayoutSchema>;
